/**
 * piCompaction — vibe-designer 1.419.0
 *
 * SPA-side context compaction for the Pi chat panel. pi-agent-core 0.73.0's
 * Agent has NO built-in compact() (that lives in pi-coding-agent's CLI layer,
 * which the SPA does not ship), but `agent.state.messages` is assignable
 * ("Assigning state.messages copies the provided top-level array" —
 * agent.d.ts:66-69). Compaction therefore is:
 *
 *   1. Serialize the conversation to a role-labeled plain-text transcript
 *      (per-message + total char caps so the summarize call itself fits).
 *   2. One NON-streaming summarize call through the same-origin chat-proxy
 *      (`/api/chat-proxy/v1/chat/completions`, Bearer = vibe_designer_token —
 *      the exact auth path the agent's own streamFn uses).
 *      `chat_template_kwargs.enable_thinking:false` rides along — reasoning
 *      models otherwise burn the whole budget in the think block and return
 *      content=null (P7.4 lesson, verified live 2026-07-02).
 *   3. Replace agent.state.messages with a 2-message compacted state:
 *      user(summary) + assistant(ack). The synthetic assistant message
 *      carries a SEEDED `usage` (chars/4 heuristic, matching
 *      contextEstimator.estimateTokens) so the token meter + WARNING banner
 *      recompute immediately — estimateContextTokens anchors on the LAST
 *      assistant usage (contextEstimator.ts:145-164); without a seeded usage
 *      it returns null and the meter blanks until the next real turn.
 *
 * FAIL-SAFE: any failure before step 3 leaves agent.state.messages UNTOUCHED
 * and throws — the caller surfaces the error; the conversation is never lost.
 *
 * The system prompt is NOT part of messages (agent.state.systemPrompt) and
 * survives compaction untouched.
 */
import type { Agent, AgentMessage } from "@earendil-works/pi-agent-core";
import type { AssistantMessage, UserMessage } from "@earendil-works/pi-ai";
import { estimateContextTokens } from "./contextEstimator";

/** Per-message transcript cap. Long tool outputs summarize fine from a prefix. */
export const TRANSCRIPT_PER_MESSAGE_CHAR_CAP = 4_000;
/** Total transcript cap (~100k tokens at chars/4). Keeps the TAIL — recent turns matter most. */
export const TRANSCRIPT_TOTAL_CHAR_CAP = 400_000;

const SUMMARIZE_SYSTEM_PROMPT =
  "You compact coding-agent conversations. Produce a dense CONTEXT SUMMARY " +
  "of the transcript that lets the agent continue seamlessly: (1) the user's " +
  "goals and constraints (verbatim where load-bearing), (2) work completed — " +
  "files written/edited with paths, builds/deploys run and their outcomes, " +
  "(3) key technical facts and decisions, (4) failures + what was learned, " +
  "(5) current state and what remains. Markdown, no preamble, no meta-" +
  "commentary about summarizing.";

/** Extract readable text from one AgentMessage (same role coverage as
 *  contextEstimator.estimateTokens: user / assistant / toolResult). */
function messageText(message: AgentMessage): { label: string; text: string } | null {
  const m = message as unknown as { role: string; content?: unknown; toolName?: string };
  const blocksText = (content: unknown): string => {
    if (typeof content === "string") return content;
    if (!Array.isArray(content)) return "";
    const parts: string[] = [];
    for (const block of content as Array<{ type: string; text?: string; thinking?: string; name?: string; arguments?: unknown }>) {
      if (block.type === "text" && block.text) parts.push(block.text);
      else if (block.type === "toolCall" && block.name)
        parts.push(`[tool_call ${block.name} ${JSON.stringify(block.arguments ?? {})}]`);
      else if (block.type === "image") parts.push("[image]");
      // thinking blocks are deliberately EXCLUDED — reasoning traces are
      // noise for a continuation summary and can be enormous.
    }
    return parts.join("\n");
  };
  switch (m.role) {
    case "user":
      return { label: "USER", text: blocksText(m.content) };
    case "assistant":
      return { label: "ASSISTANT", text: blocksText(m.content) };
    case "toolResult":
      return { label: `TOOL_RESULT(${m.toolName ?? "?"})`, text: blocksText(m.content) };
    default:
      return null; // UI-only / unknown roles contribute nothing.
  }
}

/** Role-labeled plain-text transcript with per-message + total caps.
 *  When the total cap binds, the HEAD is dropped (tail preserved). */
export function buildCompactionTranscript(messages: AgentMessage[]): string {
  const lines: string[] = [];
  for (const message of messages) {
    const entry = messageText(message);
    if (!entry || !entry.text.trim()) continue;
    let text = entry.text;
    if (text.length > TRANSCRIPT_PER_MESSAGE_CHAR_CAP) {
      text = `${text.slice(0, TRANSCRIPT_PER_MESSAGE_CHAR_CAP)}\n[...truncated ${text.length - TRANSCRIPT_PER_MESSAGE_CHAR_CAP} chars]`;
    }
    lines.push(`${entry.label}:\n${text}`);
  }
  let transcript = lines.join("\n\n---\n\n");
  if (transcript.length > TRANSCRIPT_TOTAL_CHAR_CAP) {
    transcript = `[...earlier conversation dropped to fit]\n\n${transcript.slice(transcript.length - TRANSCRIPT_TOTAL_CHAR_CAP)}`;
  }
  return transcript;
}

export interface SummarizeOptions {
  transcript: string;
  modelId: string;
  /** Same-origin chat-proxy base. Injectable for tests. */
  apiBase?: string;
  signal?: AbortSignal;
}

/** One non-streaming summarize call through the chat-proxy. Throws on any
 *  non-200 / empty-content outcome (caller keeps the original messages). */
export async function summarizeConversation(opts: SummarizeOptions): Promise<string> {
  const base = opts.apiBase ?? "/api/chat-proxy/v1";
  const token = localStorage.getItem("vibe_designer_token");
  const resp = await fetch(`${base}/chat/completions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal: opts.signal,
    body: JSON.stringify({
      model: opts.modelId,
      messages: [
        { role: "system", content: SUMMARIZE_SYSTEM_PROMPT },
        { role: "user", content: `Transcript to compact:\n\n${opts.transcript}` },
      ],
      max_tokens: 2000,
      temperature: 0.2,
      stream: false,
      chat_template_kwargs: { enable_thinking: false },
    }),
  });
  if (!resp.ok) {
    throw new Error(`compaction summarize call failed: HTTP ${resp.status}`);
  }
  const data = (await resp.json()) as {
    choices?: Array<{ message?: { content?: string | null } }>;
  };
  const content = data.choices?.[0]?.message?.content;
  if (!content || !content.trim()) {
    throw new Error("compaction summarize call returned empty content");
  }
  return content.trim();
}

export interface CompactedMessageMeta {
  api: string;
  provider: string;
  modelId: string;
}

/** The 2-message compacted state. The assistant ack carries a seeded usage
 *  (chars/4 across both messages) so contextEstimator keeps reporting. */
export function buildCompactedMessages(
  summary: string,
  meta: CompactedMessageMeta,
): AgentMessage[] {
  const userText =
    "[Conversation compacted] Context summary of everything before this point:\n\n" +
    `${summary}\n\nContinue the session from this state.`;
  const ackText = "Understood — continuing from the compacted context summary above.";
  const seededTokens = Math.ceil((userText.length + ackText.length) / 4);
  const user: UserMessage = {
    role: "user",
    content: userText,
    timestamp: Date.now(),
  };
  const assistant: AssistantMessage = {
    role: "assistant",
    content: [{ type: "text", text: ackText }],
    api: meta.api as AssistantMessage["api"],
    provider: meta.provider,
    model: meta.modelId,
    usage: {
      input: seededTokens,
      output: 0,
      cacheRead: 0,
      cacheWrite: 0,
      totalTokens: seededTokens,
      cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, total: 0 },
    },
    stopReason: "stop",
    timestamp: Date.now(),
  };
  return [user as AgentMessage, assistant as AgentMessage];
}

export interface CompactResult {
  beforeTokens: number | null;
  afterTokens: number | null;
}

/** Orchestrator: transcript → summarize → replace agent.state.messages.
 *  Throws (messages untouched) on any pre-replacement failure. */
export async function compactAgent(
  agent: Agent,
  opts?: { apiBase?: string; signal?: AbortSignal },
): Promise<CompactResult> {
  const state = (agent as unknown as {
    state: {
      messages: AgentMessage[];
      isStreaming?: boolean;
      model?: { id?: string; api?: string; provider?: string };
    };
  }).state;
  if (state.isStreaming) {
    throw new Error("cannot compact while the agent is streaming");
  }
  const messages = state.messages;
  if (!messages || messages.length === 0) {
    throw new Error("nothing to compact — the conversation is empty");
  }
  const modelId = state.model?.id;
  if (!modelId) {
    throw new Error("cannot compact — no model selected");
  }
  const beforeTokens = estimateContextTokens(messages);
  const transcript = buildCompactionTranscript(messages);
  if (!transcript.trim()) {
    throw new Error("nothing to compact — no readable conversation content");
  }
  const summary = await summarizeConversation({
    transcript,
    modelId,
    apiBase: opts?.apiBase,
    signal: opts?.signal,
  });
  const compacted = buildCompactedMessages(summary, {
    api: state.model?.api ?? "openai-completions",
    provider: state.model?.provider ?? "gamemagick-router",
    modelId,
  });
  // The ONLY mutation — everything above is read-only on failure paths.
  state.messages = compacted;
  return { beforeTokens, afterTokens: estimateContextTokens(compacted) };
}
